/**
 * # `FeedsModalsMixin.js`
 *
 * Methods for working with modals relating to Feeds/Podcasts. Generally these will make use of API methods
 * from `FeedsApiMixin.js`, or you can use the API functions directly from `feeds.js`.
 */

/* webpack-strip-block:removed */

import FeedsApiMixin from './FeedsApiMixin';

export default {
  data: () => ({
    modal_action_feed: {},
    selected_feeds: [],
    loading: {
      sync_items: false,
      sync_tags: false,
      duplicate: false,
      publish: false,
      update_files: false,
      delete: false,
      import: false,
    },
    modalCallback: null,
    modal_name_override: null,
  }),
  mixins: [FeedsApiMixin], // functions from `feeds.js` as a mixin
  methods: {
    /**
     * Fires a prompt modal which recieves a Feed URL from the user, then uses that to tell the API where to import
     * the Feed from.
     *
     * @return { boolean }
     */
    async importFeed() {
      this.modalCallback = ({ url }) => this.importFeedURL(url);
      this.loading.import = true;
      const modal_name = this.modal_name_override ?? 'feed-url-modal';

      const res = (await this.fireModal.bind(this)(modal_name));

      this.loading_import = false;

      if (res.data.confirm) return true;

      const another_res = await this.onModal(modal_name);
      return another_res?.data.confirm ?? false;
    },
    /**
     * Syncs the tags on each Feed to the Items which are part of that Feed.
     *
     * @param  {...Models.Feed} feeds
     * @return { boolean }
     */
    async confirmSyncFeedTagsToFeedItems(...feeds) {
      const store = this.selected_feeds;
      this.selected_feeds = feeds;

      this.modalCallback = () => this.syncFeedTagsToFeedItems(...feeds);

      this.loading.sync_tags = true;
      const modal_name = this.modal_name_override ?? 'sync-tags-items';
      const res = await this.fireModal(modal_name);
      this.loading.sync_tags = false;

      this.selected_feeds = store;

      if (res.data.confirm) return true; // if all went well, reload table now

      // Otherwise, there might be a case where we got no result despite the callback being triggered
      // (somehow the modal closed _during_ its callback).
      // In that case we would be interested in continuing to listen for another result, up to a timeout

      // here we await a promise which resolves instantly if we got a hit
      // or the timeout threshold is reached
      const another_res = await this.onModal(modal_name);
      return another_res?.data.confirm ?? false;
    },

    /**
     * Shows a modal detailing for each Feed, which tags that Feed has and which Items will be attached to the Feed
     * via those Tags.
     *
     * @param  {...Models.Feed} feeds
     * @return { boolean }
     */
    async confirmSyncFeedItemsByTags(...feeds) {
      // this appears to do nothing as the reference is altered anyway
      const store = this.selected_feeds;
      this.selected_feeds = feeds;

      this.modalCallback = () => this.syncFeedItemsByTags(...feeds);

      this.loading.sync_items = true;
      const modal_name = this.modal_name_override ?? 'sync-items-tags';
      const res = await this.fireModal(modal_name);
      this.loading.sync_items = false;
      this.selected_feeds = store;

      if (res.data.confirm) return true;

      const another_res = await this.onModal(modal_name);
      return another_res?.data.confirm ?? false;
    },

    /**
     * A simple confirm/cancel modal before firing destroy requests for each Feed.
     *
     * @param  {...Models.Feed} feeds
     * @return { boolean }
     */
    async confirmDestroyFeeds(...feeds) {
      this.modalCallback = () => this.destroyFeeds(...feeds);

      this.loading.delete = true;
      const modal_name = this.modal_name_override ?? 'delete-confirm';
      const res = await this.fireModal(modal_name);
      this.loading.delete = false;

      if (res.data.confirm) return true;

      const another_res = await this.onModal(modal_name);
      return another_res?.data.confirm ?? false;
    },

    /**
     * Shows a simple modal hosting Share buttons for the Feed.
     *
     * @param { Models.Feed } feed
     * @return { false }
     */
    async shareFeed(feed) {
      this.modal_action_feed = feed;
      this.fireModal(this.modal_name_override ?? `share-feed`);
      return false;
    },

    /**
     * Shows a modal which contains a themed player for the Feed.
     *
     * @param { Models.Feed } feed
     * @return { false }
     */
    previewFeed(feed) {
      this.modal_action_feed = feed;
      this.fireModal(this.modal_name_override ?? `preview-feed`);
      return false;
    },

    /**
     * A modal containing a form allowing users to create a new Item for the Feed.
     *
     * @param { Models.Feed } feed
     */
    async showItemQuickModal(feed) {
      const old_selection = [...this.selected_feeds];
      this.selected_feeds = [feed];
      await this.fireModal(this.modal_name_override ?? this.item_qc_modal);
      this.selected_feeds = old_selection;
    },
  },
};
